export enum EErrorCodes {
  consecutiveDollars,
  min,
}

import {AfterViewInit, Component, inject, Input, OnDestroy} from '@angular/core';
import {EsvgFiles} from 'frontier/nucleus';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  IDataField,
  isFormKey,
  ITemplateConfigurationForm,
  ITemplateConfigurationFormValue,
  TColorOptions,
  TContactBlock,
  TContactBlockModel,
  TCustomOptionForm,
  TCustomOptionValue,
  TFooterFormValue,
  TFormKey,
  TLogoModel,
  TNullableAttributes,
} from '../template.interface';
import {Observable, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {ConfirmationDialogComponent, IUploadDocument, UploadWrapperComponent} from 'frontier/browserkit';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {DataFieldSelectionDialogComponent} from './data-field-selection-dialog.component';
import {MatError, MatFormField, MatLabel, MatPrefix, MatSuffix} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatIcon} from '@angular/material/icon';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatOption, MatSelect} from '@angular/material/select';
import {MatSlideToggle} from '@angular/material/slide-toggle';

@Component({
  selector: 'app-templates-form',
  templateUrl: './templates-form.component.html',
  standalone: true,
  imports: [
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    MatIcon,
    MatIconButton,
    UploadWrapperComponent,
    MatSuffix,
    MatButton,
    MatSelect,
    MatOption,
    MatPrefix,
    MatSlideToggle,
    MatLabel,
    MatError,
  ],
  styleUrl: './templates-form.component.scss',
})
export class TemplatesFormComponent implements AfterViewInit, OnDestroy {
  protected readonly EsvgFiles: typeof EsvgFiles = EsvgFiles;
  protected readonly EErrorCodes: typeof EErrorCodes = EErrorCodes;

  fb: FormBuilder = inject(FormBuilder);
  dialog: MatDialog = inject(MatDialog);

  private chargeSubscription?: Subscription;

  private _form: FormGroup<ITemplateConfigurationForm> = this.fb.group<ITemplateConfigurationForm>({
    period: this.fb.control<number>(null, [Validators.min(0)]),
    charge: this.fb.control<number>(null, [Validators.min(0)]),
    defaultinterest: this.fb.control<number>(null, [Validators.min(0)]),
    account: this.fb.control<string>('', [this.noConsecutiveDollarsValidator()]),
    costlocation: this.fb.control<string>('', [this.noConsecutiveDollarsValidator()]),
    header: this.fb.group({
      title: this.fb.control('', [this.noConsecutiveDollarsValidator()]),
      subtitle: this.fb.control('', [this.noConsecutiveDollarsValidator()])
    }),
    letterbox_sender: this.fb.group({
      name: this.fb.control('', [this.noConsecutiveDollarsValidator()]),
      street: this.fb.control('', [this.noConsecutiveDollarsValidator()]),
      street_number: this.fb.control('', [this.noConsecutiveDollarsValidator()]),
      postal_code: this.fb.control('', [this.noConsecutiveDollarsValidator()]),
      city: this.fb.control('', [this.noConsecutiveDollarsValidator()]),
    }),
    bank_info: this.fb.group({
      show_qr_code: this.fb.control(false),
      name: this.fb.control('', [this.noConsecutiveDollarsValidator()]),
      iban: this.fb.control('', [this.noConsecutiveDollarsValidator()]),
      bic: this.fb.control('', [this.bicValidator(), this.noConsecutiveDollarsValidator()])
    }),
    delay: this.fb.control<number>(null, [Validators.min(0)]),
    autoarrange: this.fb.control<boolean>(false),
    autocreate: this.fb.control<boolean>(false),
    iscustomdunning: this.fb.control(false),
    dunning: this.fb.group({
      title: this.fb.control('', [this.noConsecutiveDollarsValidator()]),
      first_block_text: this.fb.control('', [this.noConsecutiveDollarsValidator()]),
      second_block_title: this.fb.control('', [this.noConsecutiveDollarsValidator()]),
      second_block_text: this.fb.control('', [this.noConsecutiveDollarsValidator()]),
      outro: this.fb.control('', [this.noConsecutiveDollarsValidator()]),
    }),
  });

  private bicValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string = control.value;
      if (!value) {
        return null;
      }

      if (value.includes('${')) {
        return null;
      }

      const bicRegex: RegExp = /^([a-zA-Z]{6}[a-zA-Z0-9]{2}([a-zA-Z0-9]{3})?)$/;
      return bicRegex.test(value) ? null : {bicInvalid: true};
    };
  }

  private noConsecutiveDollarsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string = control.value;
      if (!value) {
        return null;
      }

      if (value.includes('$$')) {
        return {consecutiveDollars: true};
      }

      return null;
    };
  }

  getErrorMessage(code: EErrorCodes): string {
    switch (code) {
      case EErrorCodes.consecutiveDollars:
        return 'Zwei aufeinanderfolgende $-Zeichen sind nicht erlaubt';
      case EErrorCodes.min:
        return 'Wert darf nicht negativ sein';
      default:
        return '';
    }
  }

  @Input() defaultForm: ITemplateConfigurationFormValue;
  @Input() isDefault: boolean;
  @Input() isInvoice: boolean;
  @Input() dataFields: IDataField[];

  @Input() set form(formValue: ITemplateConfigurationFormValue) {
    if (!formValue) {
      return;
    }

    this._form.patchValue(formValue as any);
    this.addOptionalControls(formValue);

    // Format charge to always show 2 decimal places
    if (formValue.charge != null) {
      const chargeFormatted: number = parseFloat(parseFloat(formValue.charge.toString()).toFixed(2));
      this._form.controls.charge.setValue(chargeFormatted, {emitEvent: false});

      // Update the input field display after the DOM is ready
      setTimeout(() => {
        this.updateChargeDisplay();
      }, 0);
    }

    console.log(this._form.value);
  };

  get form(): FormGroup<ITemplateConfigurationForm> {
    return this._form;
  }

  get customOptions(): TCustomOptionForm {
    return this.form.controls['custom_options'];
  };

  addContactBlockContent(name: TContactBlock): void {
    this.form.controls[name].controls.content.push(this.fb.group({
      label: this.fb.control(null),
      value: this.fb.control(null),
    }));
  }

  removeContactBlockContent(name: TContactBlock, index: number): void {
    this.confirm('Soll diese Zeile wirklich entfernt werden?')
      .pipe().subscribe((res) => {
      if (res) {
        this.form.controls[name].controls.content.removeAt(index);
      }
    })
  }

  removeNullableFormElement(element: TNullableAttributes): void {
    this.confirm('Soll das Element wirklich gelöscht werden?')
      .pipe().subscribe((res: boolean) => {
      if (res) {
        this.form.removeControl(element);
        if (this.isDefault) this.defaultForm[element] = null;
      }
    });
  }

  removeFooterItem(columnIndex: number, rowIndex: number): void {
    this.confirm('Soll das Element wirklich gelöscht werden?')
      .pipe().subscribe((res) => {
      if (res) {
        this.form.controls.footer.at(columnIndex).removeAt(rowIndex);
      }
    })
  }

  addFooterItem(columnIndex: number): void {
    this.form.controls.footer.at(columnIndex).push(this.fb.control(''));
  }

  removeFooterColumn(columnIndex: number): void {
    this.confirm('Soll die Spalte wirklich gelöscht werden?').pipe().subscribe((res) => {
      if (res) {
        this.form.controls.footer.removeAt(columnIndex);
      }
    })
  }

  addFooterColumn(): void {
    this.form.controls.footer.push(this.fb.array([this.fb.control('')]));
  }

  async onFileSelected($event: Event) {
    const arrayBuffer: ArrayBuffer = await ($event.target as HTMLInputElement).files[0].arrayBuffer();
    console.log(arrayBuffer);
    // this.form.controls.header?.controls.logo.controls.data.patchValue( arrayBuffer ?? null);
  }

  get footer(): FormArray<FormGroup<{ sections: FormArray<FormControl<string>> }>> {
    return this.form.controls["footer"] as FormArray;
  }

  getFooterColumn(index: number): FormArray<FormControl<string>> {
    return this.form.controls["footer"].at(index) as FormArray;
  }

  get contactBlock1Content() {
    return this.form.controls['contact_block1'].controls.content;
  }

  get contactBlock2Content() {
    return this.form.controls['contact_block2'].controls.content;
  }

  hasFooterConsecutiveDollarsError(): boolean {
    if (!this.form.controls.footer) {
      return false;
    }

    for (let i = 0; i < this.footer.length; i++) {
      const column = this.getFooterColumn(i);
      for (let j = 0; j < column.length; j++) {
        if (column.at(j).hasError('consecutiveDollars')) {
          return true;
        }
      }
    }
    return false;
  }

  addFooterFormControls(footer: string[][]) {
    const footerControl = this.fb.array(footer
      .map(col => this.fb.array(col
        .map(section => this.fb.control(section, [this.noConsecutiveDollarsValidator()]))) as FormArray<FormControl<string>>
      ));
    this.form.setControl('footer', footerControl);
  }

  addLogoFormGroup(value?: TLogoModel): void {
    this.form.controls.header.setControl('logo', this.fb.group({
      data: this.fb.control<IUploadDocument>(value?.data ?? null),
      width: this.fb.control<string>(value?.width ?? null),
    }))
  }

  addContactBlockFormGroup(name: TContactBlock, data?: TContactBlockModel): void {
    this.form.setControl(name, this.fb.group({
      title: this.fb.control(data?.title ?? null, [this.noConsecutiveDollarsValidator()]),
      content: this.fb.array<FormGroup<{
        label: FormControl<string>;
        value: FormControl<string>;
      }>>(data?.content ? data.content.map((el: { label: string, value: string }) => {
        return this.fb.group({
          label: new FormControl<string>(el.label, [this.noConsecutiveDollarsValidator()]),
          value: new FormControl<string>(el.value, [this.noConsecutiveDollarsValidator()]),
        })
      }) : []),
    }))
  }

  addContactNoteFormControl(value?: string): void {
    this.form.setControl('contact_note', this.fb.control(value ?? null, [this.noConsecutiveDollarsValidator()]))
  }

  addPatientPortalFormGroup(patient_portal?: { url: string, colors: TColorOptions }): void {
    this.form.setControl('patient_portal', this.fb.group({
      url: this.fb.control(patient_portal?.url ?? null, [Validators.required, this.noConsecutiveDollarsValidator()]),
      colors: this.fb.control(patient_portal?.colors ?? 'default'),
    }))
  }

  private addCustomOptionsFormGroup(custom_options: TCustomOptionValue) {
    this.form.setControl('custom_options', this.fb.array(
      custom_options.map(option => this.fb.group({
        key: this.fb.control(option.key, [this.noConsecutiveDollarsValidator()]),
        value: this.fb.control(option.value, [this.noConsecutiveDollarsValidator()]),
      }))
    ));
  }

  private addOptionalControls(res: ITemplateConfigurationFormValue) {
    if (res.footer) {
      this.addFooterFormControls(res.footer);
    } else if (this.form.controls['footer']) {
      this.form.removeControl('footer' as any);
    }

    if (res.header.logo) {
      this.addLogoFormGroup(res.header.logo);
    } else if (this.form.controls.header.controls['logo']) {
      this.form.controls.header.removeControl('logo' as any);
    }

    if (res.contact_block1) {
      const contentBlock1Model: TContactBlockModel = res.contact_block1
      this.addContactBlockFormGroup('contact_block1', contentBlock1Model);
    } else if (this.form.controls['contact_block1']) {
      this.form.removeControl('contact_block1' as any);
    }

    if (res.contact_note) {
      this.addContactNoteFormControl(res.contact_note)
    } else if (this.form.controls['contact_note']) {
      this.form.removeControl('contact_note' as any);
    }

    if (res.contact_block2) {
      const contentBlock2Model: TContactBlockModel = res.contact_block2
      this.addContactBlockFormGroup('contact_block2', contentBlock2Model);
    } else if (this.form.controls['contact_block1']) {
      this.form.removeControl('contact_block1' as any);
    }

    if (res.patient_portal) {
      this.addPatientPortalFormGroup(res.patient_portal)
    } else if (this.form.controls['patient_portal']) {
      this.form.removeControl('patient_portal' as any);
    }

    if (res.custom_options) {
      this.addCustomOptionsFormGroup(res.custom_options)
    } else if (this.form.controls['custom_options']) {
      this.form.removeControl('custom_options' as any);
    }

    if (res.iscustomdunning != null) {
      this.form.controls.iscustomdunning.setValue(res.iscustomdunning);
    }

    if (res.dunning) {
      this.form.controls.dunning.patchValue(res.dunning as any);
    } else {
      this.form.controls.dunning.patchValue({
        title: '',
        first_block_text: '',
        second_block_title: '',
        second_block_text: '',
        outro: '',
      });
    }
  }

  removeLogoForm(): void {
    this.confirm('Soll das Logo wirklich gelöscht werden?').pipe().subscribe((res) => {
      if (res) {
        this.form.controls.header.removeControl('logo');
        if (this.isDefault) this.defaultForm.header.logo = null;
      }
    })
  }

  footerDiffersFromDefault(): boolean {
    if (this.isDefault) return false;
    const formValue = this.form.value.footer;
    const formValueDefault = this.defaultForm.footer;
    let differs = false;
    if (formValue?.length !== formValueDefault?.length) return true;
    formValue.forEach((col, colIndex) => {
      if (col.length !== formValueDefault[colIndex].length) {
        differs = true;
        return;
      }
      col.forEach((section, sectionIndex) => {
        if (section !== formValueDefault[colIndex][sectionIndex]) {
          differs = true;
          return;
        }
      })
    });
    return differs;
  }

  contactBlockDiffersFromDefault(name: TContactBlock): boolean {
    if (this.isDefault) return false;
    const formValue: TContactBlockModel = this.form.value[name] as TContactBlockModel;
    const formValueDefault: TContactBlockModel = this.defaultForm[name];
    if (formValue?.title !== formValueDefault?.title) return true;
    return !this.areContactBlockContentsEqual(formValue?.content, formValueDefault?.content);
  }

  differsFromDefault(formKey: string): boolean {
    if (this.isDefault) return false;
    let formParts: string[] = formKey.split('.');
    if (!formParts.every((part: string) => isFormKey(part))) {
      throw new Error(`Invalid form key ${formKey}`);
    }
    formParts = formParts as TFormKey[];
    const formValue = formParts
      .reduce((acc: undefined | ITemplateConfigurationFormValue | TContactBlockModel | TFooterFormValue
          | TCustomOptionValue | string | string[], part: string) => {
          return acc && typeof acc === 'object' && part in acc ? (acc as any)[part] : null;
        },
        this.form.value as ITemplateConfigurationFormValue);
    const formValueDefault = formParts
      .reduce((acc, part: string) => acc ? (acc as any)[part] : null, this.defaultForm);
    return formValue !== formValueDefault;
  }

  getFormClass(formKey: string, contactBlockContentValueIndex?: number, key?: boolean): 'default' | 'overwritten' {
    if (this.isDefault) return null;
    if (formKey === 'contact_block1' || formKey === 'contact_block2') {
      if (key) {
        return this.defaultForm[formKey]?.content.map((el: {
          label: string,
          value: string
        }) => el.label).includes(this.form.value[formKey].content[contactBlockContentValueIndex].label) ?
          'default' : 'overwritten';
      }
      if (contactBlockContentValueIndex != null) {
        return this.form.value[formKey].content[contactBlockContentValueIndex].value ===
        this.defaultForm[formKey]?.content[this.getLabelIndexInDefault(formKey, contactBlockContentValueIndex)]?.value ?
          'default' : 'overwritten';
      }
      return this.areContactBlockContentsEqual((this.form.value[formKey] as TContactBlockModel)?.content,
        this.defaultForm[formKey]?.content) ? 'default' : 'overwritten';
    }
    return this.differsFromDefault(formKey) ? 'overwritten' : 'default';
  }

  resetContactBlockContentValueToDefault(name: TContactBlock, index: number): void {
    this.confirm('Soll der Wert zum Standardwert zurückgesetzt werden?')
      .pipe().subscribe((res) => {
      if (res) {
        const newValue: string = this.defaultForm[name].content[this.getLabelIndexInDefault(name, index)].value;
        this.form.controls[name].controls.content.at(index).controls.value.patchValue(newValue);
        this._form.markAsDirty();
      }
    })
  }

  getLabelIndexInDefault(name: TContactBlock, index: number): number {
    const label: string = (this.form.value[name] as TContactBlockModel)?.content[index].label;
    const defaultLabels: string[] = this.defaultForm[name]?.content.map((el: {
      label: string,
      value: string
    }) => el.label) ?? [];
    return defaultLabels.indexOf(label);
  }

  resetFieldToDefault(formKey: string): void {
    this.confirm('Soll der Wert zum Standardwert zurückgesetzt werden?')
      .pipe().subscribe((res: boolean) => {
      if (res) {
        const formParts: string[] = formKey.split('.');

        const formControl = formParts
          .reduce((acc: any, part: any) => {
            if ('controls' in acc && acc.controls && part in acc.controls && (acc.controls as any)[part]) {
              return (acc.controls as any)[part];
            }
            return null;
          }, this.form);
        const formControlDefaultValue: any = formParts
          .reduce((acc: any, part: string) => acc ? (acc as any)[part] : null, this.defaultForm);
        if (formControlDefaultValue == null) {
          // No default value available
          this.resetAnyFormControl(formParts);
        } else if (formControl === null) {
          // FormControl does not exist yet
          switch (formParts[0]) {
            case 'header':
              this.addLogoFormGroup(formControlDefaultValue as TLogoModel);
              break;
            case 'contact_block1':
              this.addContactBlockFormGroup('contact_block1');
              break;
            case 'contact_block2':
              this.addContactBlockFormGroup('contact_block2');
              break;
            case 'contact_note':
              this.addContactNoteFormControl();
              break;
            case 'patient_portal':
              this.addPatientPortalFormGroup();
              break;
          }
        } else {
          // If the whole contact block is to be reset => just set the contact block again.
          if (formParts.at(-1) === 'contact_block1' || formParts.at(-1) === 'contact_block2') {
            this.addContactBlockFormGroup(formParts.at(-1) as TContactBlock, formControlDefaultValue);
          } else {
            formControl.setValue(formControlDefaultValue);
          }
        }
        this._form.markAsDirty();
      }
    })
  }

  resetFooterToDefault(): void {
    this.confirm('Soll der Wert zum Standardwert zurückgesetzt werden?')
      .pipe().subscribe((res) => {
      if (res) {
        this.form.removeControl('footer');
        this.addFooterFormControls(this.defaultForm.footer);
        this._form.markAsDirty();
      }
    })
  }

  private resetAnyFormControl(formParts: string[]): void {
    const lastPart = formParts[formParts.length - 1];
    if (formParts.length === 1) {
      if (['contact_block1', 'contact_block2', 'contact_note', 'patient_portal', 'footer']
        .includes(lastPart)) {
        this.form.removeControl(lastPart as keyof ITemplateConfigurationForm);
        return;
      } else {
        (this.form.controls[lastPart as keyof ITemplateConfigurationForm]).reset();
      }
    } else switch (formParts[0]) {
      case 'header':
        switch (formParts[1]) {
          case 'logo':
            if (formParts.length === 2) {
              this.form.controls.header.removeControl('logo');
            } else {
              this.form.controls.header.controls.logo.get(lastPart as 'data' | 'width').reset();
            }
            break;
          default:
            this.form.controls.header
              .removeControl(lastPart as 'title' | 'subtitle');
            break;
        }
        break;
      case 'letterbox_sender':
        this.form.controls.letterbox_sender
          .removeControl(lastPart as 'name' | 'street' | 'street_number' | 'postal_code' | 'city');
        break;
      case 'contact_block1':
      case 'contact_block2':
        switch (formParts[1]) {
          case 'title':
            this.form.controls[formParts[0]]
              .get(lastPart).reset();
            break;
          case 'content':
            if (formParts.length === 2) {
              this.form.controls[formParts[0]].get('content').reset();
            } else if (formParts.length === 3) {
              if (!isNaN(Number(formParts[2]))) {
                // Index case
                this.form.controls[formParts[0]].controls.content
                  .removeAt(Number(formParts[2]));
              }
            } else {
              this.form.controls[formParts[0]].controls.content.controls[Number(formParts[2])]
                .get(lastPart as 'label' | 'value').reset();
            }
            break;
        }
        break;
      case 'patient_portal':
        switch (formParts[1]) {
          case 'url':
            this.form.controls.patient_portal
              .removeControl(lastPart as 'url');
            break;
          case 'colors':
            this.form.controls.patient_portal
              .removeControl(lastPart as 'colors');
            break;
        }
        break;
      case 'footer':
        if (formParts.length === 2) {
          this.form.controls.footer
            .removeAt(Number(formParts[1]));
        } else {
          this.form.controls.footer.controls[Number(formParts[1])]
            .removeAt(Number(formParts[2]));
        }
        break;
    }
  }

  private confirm(title: string): Observable<boolean> {
    return this.dialog.open(
      ConfirmationDialogComponent,
      {
        data: {
          title,
          confirmButtonText: 'Ja',
          cancelButtonText: 'Nein',
        }
      }
    ).afterClosed()
  }

  createCustomOption(): void {
    if (!this.customOptions) {
      const customOptionForm = this.fb.array<FormGroup<{
        key: FormControl<string>,
        value: FormControl<string>
      }>>([]) as TCustomOptionForm;
      this.form.addControl('custom_options', customOptionForm);
    }
    this.customOptions.push(this.fb.group({
      key: this.fb.control(null),
      value: this.fb.control(null),
    }));
  }

  removeCustomOption(index: number) {
    this.confirm('Soll das Element wirklich gelöscht werden?')
      .pipe().subscribe((res: boolean): void => {
      if (res) {
        this.customOptions.removeAt(index);
      }
    });
  }

  areContactBlockContentsEqual(specificContent: { label: string; value: string }[], defaultContent: {
    label: string;
    value: string
  }[]): boolean {
    if (specificContent?.length !== defaultContent?.length) {
      return false;
    } else {
      for (let i: number = 0; i < specificContent.length; i++) {
        if (JSON.stringify(specificContent) !== JSON.stringify(defaultContent)) {
          return false;
        }
      }
      return true;
    }
  }

  onInput(event: Event): void {
    if ((event as InputEvent).data === '$' && this.dataFields && this.dataFields.length > 0) {
      const dialogRef: MatDialogRef<DataFieldSelectionDialogComponent>
        = this.dialog.open(DataFieldSelectionDialogComponent, {
        data: {dataFields: this.dataFields}
      });

      dialogRef.afterClosed().subscribe((res: string): void => {
        if (res) {
          (event.target as HTMLInputElement).value
            = (event.target as HTMLInputElement).value.slice(0, (event.target as HTMLInputElement).selectionStart)
            + '{' + res + '}'
            + (event.target as HTMLInputElement).value.slice((event.target as HTMLInputElement).selectionStart);
        }
      });
    }
  }

  getLogoSrc(): string | ArrayBuffer {
    const imgData: string | ArrayBuffer = this.form.value.header.logo.data.data;
    const includesBase64Prefix: boolean = (imgData as string).startsWith('data:image/png;base64,');
    return includesBase64Prefix ? imgData : 'data:image/png;base64,' + imgData;
  }

  limitDecimalPlaces(event: Event, maxDecimalPlaces: number, fieldName: string): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    let value: string = input.value;

    // Allow empty value
    if (!value) return;

    // For period (maxDecimalPlaces = 0), prevent decimal point and comma entirely
    if (maxDecimalPlaces === 0) {
      value = value.replace(/[.,]/g, '');
      this.form.get(fieldName).setValue(parseFloat(value), {emitEvent: false});
      return;
    }

    // Check if the value contains a decimal point
    const decimalIndex: number = value.indexOf('.');
    if (decimalIndex !== -1) {
      const decimalPart: string = value.substring(decimalIndex + 1);
      if (decimalPart.length > maxDecimalPlaces) {
        // Truncate to max decimal places
        this.form.get(fieldName).setValue(value.substring(0, decimalIndex + maxDecimalPlaces + 1));
      }
    }
  }

  onChargeFocus(event: FocusEvent): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const value: number = this.form.controls.charge.value;
    if (value != null) {
      // Keep the formatted value (with two decimal places) when focusing
      input.value = parseFloat(value.toString()).toFixed(2);
    }
  }

  formatCharge(event: FocusEvent): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    let value: number = parseFloat(input.value);

    if (input.value === '' || input.value == null || isNaN(value)) {
      // Set to 0 if empty
      input.value = '0.00';
      this.form.controls.charge.setValue(0, {emitEvent: false});
      this.form.controls.charge.updateValueAndValidity();
      return;
    }

    // Always format to 2 decimal places
    const formatted: string = value.toFixed(2);
    input.value = formatted;
    this.form.controls.charge.setValue(parseFloat(formatted), {emitEvent: false});
    // Trigger validation
    this.form.controls.charge.updateValueAndValidity();
  }

  updateChargeDisplay(): void {
    const chargeInput: HTMLInputElement
      = document.querySelector('input[formControlName="charge"]') as HTMLInputElement;
    const chargeValue: number = this.form.controls.charge.value;

    if (chargeInput && chargeValue != null && document.activeElement !== chargeInput) {
      // Only update if the field is not currently focused
      chargeInput.value = parseFloat(chargeValue.toString()).toFixed(2);
    }
  }

  setToZeroIfEmpty(event: FocusEvent, fieldName: 'period' | 'defaultinterest' | 'delay'): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const value: number = parseFloat(input.value);

    if (input.value === '' || input.value == null || isNaN(value)) {
      // Set to 0 if empty
      input.value = '0';
      this.form.controls[fieldName].setValue(0, {emitEvent: false});
      this.form.controls[fieldName].updateValueAndValidity();
    }
  }

  ngAfterViewInit(): void {
    // Subscribe to charge value changes to update the display
    this.chargeSubscription = this._form.controls.charge.valueChanges
      .pipe(debounceTime(100))
      .subscribe(() => {
        setTimeout(() => this.updateChargeDisplay(), 0);
      });
  }

  ngOnDestroy(): void {
    this.chargeSubscription?.unsubscribe();
  }
}
